import React, { useEffect, useMemo, useRef } from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Flex } from "antd";

import { SharingRights } from "@/shared/utils/constants";
import { useDataBlockDependencies } from "@/domains/datablocks/hooks/useDataBlockDependencies";
import { useFieldVisibility } from "@/domains/datablocks/hooks/useFieldVisibility";
import FieldRenderer from "@/domains/datablocks/fields/FieldRenderer";
import {
  ContentType,
  DataFieldResponse,
  DisplayType,
  FieldType,
  ValidationRuleTypes,
} from "@/redux/datablocks/types";
import {
  DataBlockDetailsStepProps,
  DataBlockFormFields,
  FormFieldsWithSharingRights,
} from "@/domains/datablocks/types";

import "../styles.css";

const DataBlockDetailsForm = ({
  dataTemplate,
  excludeFields = [],
  dataBlock,
}: DataBlockDetailsStepProps) => {
  const { t } = useTranslation();
  const { control, setValue } = useFormContext<DataBlockFormFields>();
  const dataBlockDependencies = useDataBlockDependencies(dataTemplate);
  const hiddenFieldIds = useFieldVisibility(dataTemplate, control);
  const prevHiddenFieldIds = useRef<Set<number>>(new Set());

  const existingFieldValuesMap = useMemo(() => {
    if (!dataBlock) return new Map<number, boolean>();
    return new Map(
      dataBlock.fieldValues.map((fieldValue) => {
        const hasValue =
          fieldValue.value != null &&
          fieldValue.value !== "" &&
          (Array.isArray(fieldValue.value)
            ? fieldValue.value.length > 0
            : true);

        return [fieldValue.field, hasValue];
      }),
    );
  }, [dataBlock]);

  useEffect(() => {
    dataTemplate.templateFields.forEach((field, index) => {
      const isNowHidden = hiddenFieldIds.has(field.id);
      const wasPreviouslyHidden = prevHiddenFieldIds.current.has(field.id);

      if (isNowHidden && !wasPreviouslyHidden) {
        setValue(`fieldValues.${index}.value`, null);
      }
    });

    prevHiddenFieldIds.current = new Set(hiddenFieldIds);
  }, [hiddenFieldIds, dataTemplate.templateFields, setValue]);

  const fieldIdToIndexMap = useMemo(() => {
    return new Map(
      dataTemplate.templateFields.map((field, index) => [field.id, index]),
    );
  }, [dataTemplate]);

  const fields: FormFieldsWithSharingRights[] = useMemo(() => {
    const isEditMode = !!dataBlock;

    const filteredFields = dataTemplate.templateFields.filter(
      (field: DataFieldResponse) => !field.disabled,
    );

    const newFields: FormFieldsWithSharingRights[] = [
      ...filteredFields.map((field: DataFieldResponse) => {
        const originalIndex = fieldIdToIndexMap.get(field.id) ?? 0;
        return {
          ...field,
          name: `fieldValues.${originalIndex}.value`, // Use original index to match fieldValues array structure
          isReadOnly:
            isEditMode &&
            field.disabled &&
            existingFieldValuesMap.get(field.id) === true,
        };
      }),
    ];

    newFields.splice(4, 0, {
      id: "sharingRights",
      name: "sharingRights",
      disabled: false,
      allowsCustomOptions: false,
      placeholder: t("sharingRights.selectPlaceholder"),
      contentType: ContentType.Options,
      options: SharingRights.asList().map((option: Record<string, string>) => ({
        id: option.value,
        label: option.label,
      })),
      order: 4,
      rules: [
        {
          type: ValidationRuleTypes.REQUIRED,
          dependentOn: null,
          errorMessage: "ruleRequired",
        },
      ],
      description: "",
      title: "Sharing Rights",
      type: FieldType.SELECT,
      displayType: DisplayType.DROPDOWN,
      multiple: false,
    });

    return newFields
      .filter((i) => !excludeFields.includes(i.id))
      .filter((i) => !hiddenFieldIds.has(i.id as number));
  }, [
    dataTemplate,
    excludeFields,
    hiddenFieldIds,
    dataBlock,
    existingFieldValuesMap,
    fieldIdToIndexMap,
  ]);

  const eitherRequiredRules: Record<number, -1 | 0 | 1> = useMemo(() => {
    const rules = dataTemplate.validationRules
      .filter((rule) => rule.type === ValidationRuleTypes.EITHER_REQUIRED)
      .map((rule) => rule.ruleFields);

    const orderFieldIds = fields.map((field) => field.id);

    const orderedRules = rules.map((rule) =>
      rule
        .slice()
        .sort((a, b) => orderFieldIds.indexOf(a) - orderFieldIds.indexOf(b)),
    );

    return orderedRules.reduce(
      (acc, fieldIds) => {
        fieldIds.forEach((fieldId: number) => {
          const fieldIndex = fieldIds.indexOf(fieldId);
          acc[fieldId] =
            fieldIndex === 0 ? -1 : fieldIndex === fieldIds.length - 1 ? 1 : 0;
        });
        return acc;
      },
      {} as Record<number, -1 | 0 | 1>,
    );
  }, [dataTemplate, fields]);

  const dependentRequiredFields = useMemo(() => {
    const fieldIds = new Set<number>();
    dataTemplate.validationRules
      .filter((rule) => rule.type === ValidationRuleTypes.DEPENDENT_REQUIRED)
      .forEach((rule) => {
        rule.ruleFields?.forEach((fieldId) => fieldIds.add(fieldId));
      });
    return fieldIds;
  }, [dataTemplate]);

  return (
    <Flex vertical gap={20}>
      {fields.map((field) => (
        <FieldRenderer
          key={`field-${field.id}-${dataTemplate.id}`}
          field={field}
          control={control}
          dataBlockDependencies={dataBlockDependencies}
          eitherRequiredRules={eitherRequiredRules}
          dependentRequiredFields={dependentRequiredFields}
          isReadOnly={field.isReadOnly}
        />
      ))}
    </Flex>
  );
};

export default React.memo(DataBlockDetailsForm);
